/** @format — Attach tracking.map when orchestrator returns shipment.list only */

import { trackingService } from "@/services/trackingService";
import {
  buildStreamPayload,
  buildStreamState,
  buildUserStreamMessage,
  createAguiIds,
} from "./aguiStream";
import { filterKnownUiComponents } from "./normalizeComponents";
import { streamTrackingByCode } from "../tracking/trackingStream";

const TRACKING_INTENT_RE =
  /\b(track|tracking|where is|locate|find my shipment|active shipment|last active|shipment location|on the map)\b/i;

const CODE_IN_TEXT_RE = /\b(US\d{6,}|S\d{6,}|#[A-Z0-9-]+)\b/i;

function normalizeStatus(status) {
  return String(status ?? "")
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function isInTransitStatus(status) {
  const key = normalizeStatus(status);
  return key.includes("in_transit") || key === "in_transit" || key === "on_vessel";
}

export function isTrackingIntent(userText) {
  return TRACKING_INTENT_RE.test(String(userText ?? ""));
}

/** Listing shipments — keep shipment.list, do not force map-only UI */
export function isListShipmentsIntent(userText) {
  return /\b(what shipments|which shipments|list.*shipment|my shipments|all shipments|shipments do i have)\b/i.test(
    String(userText ?? ""),
  );
}

/** Track / locate — show map card only in chat (no list + no summary footer) */
export function isTrackLocateIntent(userText) {
  return isTrackingIntent(userText) && !isListShipmentsIntent(userText);
}

/** Chat UI: map-only for locate; list allowed for inventory questions */
export function filterChatTrackingUi(uiComponents = [], userText) {
  const ui = filterKnownUiComponents(uiComponents);
  if (!isTrackLocateIntent(userText)) return ui;
  if (hasTrackingMapComponent(ui)) {
    return ui.filter((b) => b.name === "tracking.map");
  }
  return ui.filter((b) => b.name !== "shipment.list" && b.name !== "tracking.summary");
}

export function hasTrackingMapComponent(uiComponents = []) {
  return filterKnownUiComponents(uiComponents).some((b) => b.name === "tracking.map");
}

/** Orchestrator prose that contradicts a successful tracking.map card */
export function isShipmentNotFoundProse(text) {
  return /\b(couldn't find|could not find|cannot find|no shipments? matching|not find any shipment|didn't find any shipment)\b/i.test(
    String(text ?? ""),
  );
}

/**
 * When track-by-code returned a map but the chat agent said "not found", prefer map truth.
 */
export function reconcileTextWithTrackingMap(text, { bookingCode, vehicle } = {}) {
  const cleaned = String(text ?? "").trim();
  if (!isShipmentNotFoundProse(cleaned)) return cleaned;
  const ref = bookingCode ? String(bookingCode).replace(/^#/, "").trim() : "";
  const label = ref && vehicle ? `${ref} (${vehicle})` : ref || vehicle || "this shipment";
  return `Here's the latest location for ${label}.`;
}

export function extractTrackingCodeFromText(text) {
  const match = String(text ?? "").match(CODE_IN_TEXT_RE);
  if (!match) return null;
  return match[1].replace(/^#/, "").trim();
}

/** Prefer in-transit row from shipment.list AG-UI props */
export function pickTrackingCodeFromShipmentList(uiComponents = []) {
  const listBlock = filterKnownUiComponents(uiComponents).find((b) => b.name === "shipment.list");
  const rows = listBlock?.props?.shipments;
  if (!Array.isArray(rows) || !rows.length) return null;

  const inTransit = rows.find((s) => isInTransitStatus(s.status ?? s.detailedStatus));
  const pick = inTransit ?? rows[0];
  const ref = pick?.reference ?? pick?.bookingId ?? pick?.bookingRef ?? pick?.id;
  return ref ? String(ref).replace(/^#/, "").trim() : null;
}

async function pickFromActiveShipmentsApi() {
  try {
    const data = await trackingService.getActiveShipments(10);
    const rows = data?.shipments ?? [];
    if (!rows.length) return null;
    const inTransit = rows.find((r) => isInTransitStatus(r.status));
    const pick = inTransit ?? rows[0];
    return (
      pick?.bookingId ??
      String(pick?.shipmentRef ?? "").replace(/^#/, "") ??
      pick?.shipmentId ??
      null
    );
  } catch {
    return null;
  }
}

export async function resolveTrackingCodeForMap(
  userText,
  uiComponents = [],
  { shipmentBookingId } = {},
) {
  const fromList = pickTrackingCodeFromShipmentList(uiComponents);
  if (fromList) return fromList;

  const fromText = extractTrackingCodeFromText(userText);
  if (fromText) return fromText;

  if (shipmentBookingId) {
    return String(shipmentBookingId).replace(/^#/, "").trim();
  }

  if (/active|last.*shipment|my shipment/i.test(String(userText ?? ""))) {
    return pickFromActiveShipmentsApi();
  }

  return null;
}

/**
 * Stream track-by-code and return tracking.map / tracking.summary blocks.
 */
export async function fetchTrackingMapUiBlocks(
  trackingCode,
  { threadId, userId, timeZone, offsetMinutes, shipmentId = null } = {},
) {
  const code = String(trackingCode ?? "").trim();
  if (!code) return [];

  const { runId, messageId } = createAguiIds();
  const scoped = Boolean(shipmentId);
  const payload = buildStreamPayload({
    threadId: threadId ?? createAguiIds().threadId,
    runId,
    messages: [
      buildUserStreamMessage(`Track shipment ${code}`, userId ?? "anonymous", messageId),
    ],
    state: buildStreamState({
      sessionId: threadId,
      chatId: threadId,
      newSession: false,
      timeZone,
      offsetMinutes,
      shipmentChatScope: scoped,
      currentShipmentId: shipmentId,
    }),
    forwardedProps: scoped
      ? {
          shipment_chat_scope: true,
          shipment_id: shipmentId,
          current_shipment_id: shipmentId,
          tracking_code: code,
          booking_id: code,
        }
      : {},
  });

  const result = await streamTrackingByCode(code, payload, {});
  const blocks = filterKnownUiComponents(result?.uiComponents ?? []);
  return blocks.filter((b) => b.name === "tracking.map");
}

/** Prepend map card when user asked to track but orchestrator only returned shipment.list */
export async function enrichUiWithTrackingMap(uiComponents, userText, streamContext = {}) {
  let ui = filterKnownUiComponents(uiComponents);
  if (!isTrackLocateIntent(userText)) {
    return ui;
  }

  if (!hasTrackingMapComponent(ui)) {
    const code = await resolveTrackingCodeForMap(userText, ui, streamContext);
    if (code) {
      try {
        const mapBlocks = await fetchTrackingMapUiBlocks(code, streamContext);
        if (mapBlocks.length) ui = [...mapBlocks, ...ui];
      } catch {
        /* keep orchestrator ui */
      }
    }
  }

  return filterChatTrackingUi(ui, userText);
}

export function createEphemeralTrackingThreadId() {
  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  return `trk_${id}`;
}
